import { SelectGroup } from "@appretio/ui-kit";
import React, { useCallback } from "react";
import type { TemporalContext, TemporalContextField } from "./temporalConfig";
import { type TemporalConfig, temporalConfig } from "./temporalConfig";
import { TemporalContextDropdown } from "./TemporalContextDropdown";

interface TemporalContextSelectorProps {
  /**
   * Currently selected options for each temporal context field
   */
  selected?: TemporalContext;
  /**
   * Sets selected options for each temporal context field
   */
  setSelected: React.Dispatch<React.SetStateAction<TemporalContext>>;
  /**
   * The fields' options; defaults to the shared `temporalConfig`
   */
  config?: Record<TemporalContextField, TemporalConfig>;
  /**
   * Shows each field's label as the dropdown placeholder instead of above it
   */
  labelAsPlaceholder?: boolean;
  /**
   * Whether the dropdowns filter their options as the user types
   */
  typeToSearch?: boolean;
}

export function TemporalContextSelector({
  selected,
  setSelected,
  config = temporalConfig,
  labelAsPlaceholder = false,
  typeToSearch = true
}: TemporalContextSelectorProps) {
  // Memoize onChange handler
  const handleSetSelected = useCallback(
    (field: TemporalContextField, value: string | string[] | undefined) => {
      // Clearing a dropdown emits "", which the request context rejects —
      // drop the field instead.
      setSelected((prev) => ({
        ...prev,
        [field]: value === "" ? undefined : value
      }));
    },
    [setSelected]
  );

  return (
    <SelectGroup>
      {(
        Object.entries(config) as Array<[TemporalContextField, TemporalConfig]>
      ).map(([field, fieldConfig]) => {
        if (field === "seasonal") {
          return null; // Skip rendering for "seasonal" field since it's handled separately
        }

        const selectedOptions = selected?.[field];

        return (
          <TemporalContextDropdown
            key={field}
            field={field}
            config={fieldConfig}
            selected={selectedOptions}
            setSelected={handleSetSelected}
            labelAsPlaceholder={labelAsPlaceholder}
            typeToSearch={typeToSearch}
          />
        );
      })}
    </SelectGroup>
  );
}
